import type { RouteObject } from "react-router";
import { MorePage } from "../features/more/MorePage";
import { SettingsPage } from "../features/more/SettingsPage";
import { SyncIndicator, SyncStatusBar } from "../offline/SyncStatusBar";
import { AppUrlNavigation } from "../platform/AppUrlNavigation";
import { OpenDaysBadge } from "../platform/OpenDaysBadge";
import { CompactClock } from "../features/clock/CompactClock";
import { GeofenceReminderSync } from "../features/geofence/GeofenceReminderSync";
import { setupRoute } from "../features/setup/setupRoutes";
import { createPlatformRoute } from "../features/platform/platformRoutes";
import { UpdateBanner } from "../pwa/UpdateBanner";
import { Loading } from "../shared/ui/Loading";
import { AppLayout } from "./AppLayout";
import { NotFoundPage, RouteErrorPage, ScreenErrorPage } from "./ErrorPage";
import { lazyScreen } from "./lazyScreen";
import { LoginCallbackPage } from "./LoginCallbackPage";
import { routePaths } from "./navigation";
import { RequireAccess } from "./RequireAccess";
import { RequireAuth } from "./RequireAuth";
import { screenAccess } from "./routeAccess";
import type { AccessRight } from "../shared/auth/accessRights";
import { StartPage } from "./StartPage";

/**
 * Puts restricted screens behind the route guard (UX-025): opened by a link or an address without
 * one of the rights, they show a neutral „Keine Berechtigung“ page inside the app shell.
 *
 * @param anyOf - Rights of which one opens the screens.
 * @param children - The screens.
 * @returns The guarded route.
 */
function guarded(anyOf: readonly AccessRight[], children: RouteObject[]): RouteObject {
  const route: RouteObject = { element: <RequireAccess anyOf={anyOf} />, children };

  return route;
}

/** Options of {@link createAppRoutes}. */
export interface AppRouteOptions {
  /**
   * Whether the billing page and the registration of a company are registered; `false` in the
   * native apps, so that neither a link nor an app URL leads to a way to buy (ADR-0087).
   */
  readonly purchasePaths: boolean;
}

/**
 * Creates the routes of the registration of a company (T-132); public, no sign-in.
 *
 * @returns The routes.
 */
function signupRoutes(): RouteObject[] {
  const routes: RouteObject[] = [
    {
      path: routePaths.signup,
      errorElement: <RouteErrorPage />,
      hydrateFallbackElement: <Loading />,
      lazy: lazyScreen(
        () => import("../features/signup/SignupPage"),
        (m) => m.SignupPage,
      ),
    },
    {
      path: routePaths.signupVerify,
      errorElement: <RouteErrorPage />,
      hydrateFallbackElement: <Loading />,
      lazy: lazyScreen(
        () => import("../features/signup/SignupVerifyPage"),
        (m) => m.SignupVerifyPage,
      ),
    },
  ];

  return routes;
}

/**
 * Creates the route of the billing page (T-134 to T-137).
 *
 * @returns The routes.
 */
function billingRoutes(): RouteObject[] {
  const routes: RouteObject[] = [
    {
      path: routePaths.billing,
      lazy: lazyScreen(
        () => import("../features/billing/BillingPage"),
        (m) => m.BillingPage,
      ),
    },
  ];

  return routes;
}

/**
 * Creates the route of „Verwaltung“ (master data) with its areas.
 *
 * @returns The route.
 */
function adminRoute(): RouteObject {
  const route: RouteObject = {
    path: routePaths.admin,
    lazy: lazyScreen(
      () => import("../features/admin/adminScreens"),
      (m) => m.AdminPage,
    ),
    children: [
      {
        index: true,
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.EmployeesAdmin,
        ),
      },
      {
        path: "employees/:employeeId",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.EmployeeDetail,
        ),
      },
      {
        path: "absences",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.AbsenceRequestsAdmin,
        ),
      },
      {
        path: "establishments",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.EstablishmentsAdmin,
        ),
      },
      {
        path: "teams",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.TeamsAdmin,
        ),
      },
      {
        path: "models",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.WorkTimeModelsAdmin,
        ),
      },
      {
        path: "exports",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.ExportsAdmin,
        ),
      },
      {
        path: "profiles",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.RuleProfilesAdmin,
        ),
      },
      {
        path: "exports/month-closing",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.MonthClosingAdmin,
        ),
      },
      {
        path: "absences/planning",
        lazy: lazyScreen(
          () => import("../features/admin/adminScreens"),
          (m) => m.LeavePlanningAdmin,
        ),
      },
    ],
  };

  return route;
}

/**
 * Creates the route tree. Every screen lives below the authentication guard; the login callback
 * and the registration of a company are public. The root element follows URLs opened in the native
 * apps, which register neither the billing page nor the registration (ADR-0087).
 *
 * Inside the app shell the screens share one error element, so a failing screen, also one whose
 * code cannot be loaded offline, replaces only the content and keeps the app bar with the
 * connectivity status and the navigation (UX-109). Restricted screens sit behind the route guard
 * (UX-025).
 *
 * @param options - Platform-dependent parts of the tree.
 * @returns The routes.
 */
export function createAppRoutes(options: AppRouteOptions): RouteObject[] {
  const signupScreens = options.purchasePaths ? signupRoutes() : [];
  const billingScreens = options.purchasePaths
    ? [guarded(screenAccess.billing, billingRoutes())]
    : [];
  const routes: RouteObject[] = [
    {
      // Opens paths delivered through the URI scheme of the native apps (T-080).
      element: <AppUrlNavigation />,
      children: [
        {
          path: routePaths.loginCallback,
          element: <LoginCallbackPage />,
          errorElement: <RouteErrorPage />,
        },
        ...signupScreens,
        {
          // A shared terminal device (T-173, ADR-0142): its own device token, never a person's session.
          path: routePaths.terminal,
          errorElement: <RouteErrorPage />,
          hydrateFallbackElement: <Loading />,
          lazy: lazyScreen(
            () => import("../features/terminal/TerminalPage"),
            (m) => m.TerminalPage,
          ),
        },
        {
          element: <RequireAuth />,
          errorElement: <RouteErrorPage />,
          children: [
            // The operator area (F-69) with its own shell; `PlatformGate` lets only operator staff in.
            createPlatformRoute(),
            {
              element: (
                <AppLayout
                  indicator={<SyncIndicator />}
                  clock={<CompactClock />}
                  status={
                    <>
                      <UpdateBanner />
                      <SyncStatusBar />
                      <OpenDaysBadge />
                      <GeofenceReminderSync />
                    </>
                  }
                />
              ),
              // Errors of the shell itself; errors of a screen are handled inside the shell below.
              errorElement: <RouteErrorPage />,
              // Shown while the code of a lazily loaded screen is fetched on the first navigation.
              hydrateFallbackElement: <Loading />,
              children: [
                {
                  errorElement: <ScreenErrorPage />,
                  children: [
                    { index: true, element: <StartPage /> },
                    // Not lazy: small, and the only way to sign out on phones.
                    { path: routePaths.more, element: <MorePage /> },
                    // Not lazy either: the account menu leads here to sign out (F-74).
                    { path: routePaths.settings, element: <SettingsPage /> },
                    guarded(screenAccess.ownRecords, [
                      {
                        path: routePaths.times,
                        lazy: lazyScreen(
                          () => import("../features/times/TimesPage"),
                          (m) => m.TimesPage,
                        ),
                      },
                      {
                        // Everyone while rostering is active (T-178); only published shifts.
                        path: routePaths.myRoster,
                        lazy: lazyScreen(
                          () => import("../features/roster/MyRosterPage"),
                          (m) => m.MyRosterPage,
                        ),
                      },
                      {
                        // Planners and HR (T-176); the page checks the right per establishment.
                        path: routePaths.roster,
                        lazy: lazyScreen(
                          () => import("../features/roster/RosterPage"),
                          (m) => m.RosterPage,
                        ),
                      },
                      {
                        path: routePaths.balances,
                        lazy: lazyScreen(
                          () => import("../features/balances/BalancesPage"),
                          (m) => m.BalancesPage,
                        ),
                      },
                      {
                        path: routePaths.corrections,
                        lazy: lazyScreen(
                          () => import("../features/corrections/CorrectionsPage"),
                          (m) => m.CorrectionsPage,
                        ),
                      },
                      {
                        path: routePaths.absences,
                        lazy: lazyScreen(
                          () => import("../features/absences/AbsencesPage"),
                          (m) => m.AbsencesPage,
                        ),
                      },
                    ]),
                    guarded(screenAccess.team, [
                      {
                        path: routePaths.team,
                        lazy: lazyScreen(
                          () => import("../features/team/TeamPage"),
                          (m) => m.TeamPage,
                        ),
                      },
                    ]),
                    guarded(screenAccess.violations, [
                      {
                        path: routePaths.violations,
                        lazy: lazyScreen(
                          () => import("../features/violations/ViolationsPage"),
                          (m) => m.ViolationsPage,
                        ),
                      },
                    ]),
                    guarded(screenAccess.masterData, [setupRoute(), adminRoute()]),
                    guarded(screenAccess.projects, [
                      {
                        path: routePaths.projects,
                        lazy: lazyScreen(
                          () => import("../features/projects/ProjectsPage"),
                          (m) => m.ProjectsPage,
                        ),
                      },
                    ]),
                    guarded(screenAccess.tenant, [
                      {
                        path: routePaths.tenant,
                        lazy: lazyScreen(
                          () => import("../features/tenant/TenantPage"),
                          (m) => m.TenantPage,
                        ),
                      },
                    ]),
                    ...billingScreens,
                    {
                      path: routePaths.privacy,
                      lazy: lazyScreen(
                        () => import("../features/privacy/PrivacyPage"),
                        (m) => m.PrivacyPage,
                      ),
                    },
                    {
                      path: routePaths.install,
                      lazy: lazyScreen(
                        () => import("../features/install/InstallPage"),
                        (m) => m.InstallPage,
                      ),
                    },
                    { path: "*", element: <NotFoundPage /> },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
  ];

  return routes;
}
